/**
 * Alpine.js Store for Guest Manager
 *
 * Reactive state management for selection, filters, sorting, and modals.
 * HTMX handles server communication; Alpine handles UI state.
 */

/**
 * Get event ID from URL
 */
function getEventId(): string | null {
  const match = window.location.pathname.match(/\/events\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Get tenant slug from URL
 */
function getSlug(): string | null {
  const match = window.location.pathname.match(/^\/([^/]+)\/events/);
  return match ? match[1] : null;
}

/**
 * Build URL for guest list with current filters and sort
 */
function buildGuestListUrl(
  filter: { type: string; value: string },
  sort: { column: string; order: string },
  searchQuery: string
): string {
  const eventId = getEventId();
  const slug = getSlug();

  const params = new URLSearchParams();

  if (searchQuery) {
    params.set('q', searchQuery);
  }

  if (filter.type === 'group' && filter.value) {
    params.set('group', filter.value);
  } else if (filter.type === 'rsvp' && filter.value) {
    params.set('rsvp', filter.value);
  } else if (filter.type === 'vip' && filter.value) {
    params.set('vip', filter.value);
  }

  if (sort.column) {
    params.set('sort', sort.column);
    params.set('order', sort.order);
  }

  return `/${slug}/events/${eventId}/guests/rows?${params.toString()}`;
}

export interface GuestManagerStore {
  // Selection state
  selectedIds: Set<string>;
  readonly selectionCount: number;
  allSelected: boolean;

  // Filter state
  filter: { type: string; value: string };

  // Sort state
  sort: { column: string; order: string };

  // Modal state
  tableModalOpen: boolean;
  rsvpPickerOpen: boolean;

  // Selection methods
  toggleSelection(id: string): void;
  selectAll(): void;
  clearSelection(): void;
  isSelected(id: string): boolean;

  // Filter methods
  setFilter(type: string, value: string): void;

  // Sort methods
  setSortColumn(column: string): void;

  // Modal methods
  openTableModal(): void;
  closeTableModal(): void;
  toggleRsvpPicker(): void;
  closeRsvpPicker(): void;

  // HTMX integration
  triggerRefresh(): void;
  reapplySelectionState(): void;

  // Internal
  _getSearchQuery(): string;
}

/**
 * Create the guest manager Alpine store
 */
export function createGuestManagerStore(): GuestManagerStore {
  return {
    // State
    selectedIds: new Set<string>(),
    allSelected: false,
    filter: { type: 'all', value: '' },
    sort: { column: 'name', order: 'asc' },
    tableModalOpen: false,
    rsvpPickerOpen: false,

    // Computed - selectionCount getter
    get selectionCount(): number {
      return this.selectedIds.size;
    },

    // Selection methods
    toggleSelection(id: string): void {
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
      }
      // Force Alpine to detect the change by creating a new Set
      this.selectedIds = new Set(this.selectedIds);
      this.updateRowVisualState(id);
      this.updateSelectAllState();
    },

    selectAll(): void {
      const allRows = document.querySelectorAll<HTMLElement>('[data-guest-id]');
      const allSelected = this.selectedIds.size === allRows.length && allRows.length > 0;

      if (allSelected) {
        this.selectedIds = new Set();
      } else {
        const newSet = new Set<string>();
        allRows.forEach((row) => {
          const guestId = row.dataset.guestId;
          if (guestId) {
            newSet.add(guestId);
          }
        });
        this.selectedIds = newSet;
      }

      this.reapplySelectionState();
      this.updateSelectAllState();
    },

    clearSelection(): void {
      this.selectedIds = new Set();
      this.reapplySelectionState();
      this.updateSelectAllState();
      this.closeRsvpPicker();
    },

    isSelected(id: string): boolean {
      return this.selectedIds.has(id);
    },

    // Filter methods
    setFilter(type: string, value: string): void {
      this.filter = { type, value };

      // Update hidden filter inputs for HTMX include
      const groupInput = document.getElementById('filter-group') as HTMLInputElement | null;
      const rsvpInput = document.getElementById('filter-rsvp') as HTMLInputElement | null;
      const vipInput = document.getElementById('filter-vip') as HTMLInputElement | null;

      if (groupInput) groupInput.value = type === 'group' ? value : '';
      if (rsvpInput) rsvpInput.value = type === 'rsvp' ? value : '';
      if (vipInput) vipInput.value = type === 'vip' ? value : '';

      this.triggerRefresh();
    },

    // Sort methods
    setSortColumn(column: string): void {
      if (this.sort.column === column) {
        this.sort = {
          column,
          order: this.sort.order === 'asc' ? 'desc' : 'asc',
        };
      } else {
        this.sort = { column, order: 'asc' };
      }
      this.triggerRefresh();
    },

    // Modal methods
    openTableModal(): void {
      this.tableModalOpen = true;
      document.body.style.overflow = 'hidden';
    },

    closeTableModal(): void {
      this.tableModalOpen = false;
      document.body.style.overflow = '';
    },

    toggleRsvpPicker(): void {
      this.rsvpPickerOpen = !this.rsvpPickerOpen;
    },

    closeRsvpPicker(): void {
      this.rsvpPickerOpen = false;
    },

    // HTMX integration
    triggerRefresh(): void {
      const isMobile = window.innerWidth < 768;
      const target = isMobile ? '#guest-list' : '#guest-table-body';
      const url = buildGuestListUrl(this.filter, this.sort, this._getSearchQuery());

      (window.htmx as any).ajax('GET', url, {
        target: target,
        swap: 'innerHTML',
        headers: {
          'X-Viewport': isMobile ? 'mobile' : 'desktop',
        },
      });
    },

    // Re-apply selection state to DOM after HTMX swaps
    reapplySelectionState(): void {
      document.querySelectorAll<HTMLElement>('[data-guest-id]').forEach((row) => {
        const guestId = row.dataset.guestId;
        if (guestId) {
          row.classList.toggle('selected', this.selectedIds.has(guestId));
        }
      });
    },

    // Helper to update single row visual state
    updateRowVisualState(id: string): void {
      const row = document.querySelector<HTMLElement>(`[data-guest-id="${id}"]`);
      if (row) {
        row.classList.toggle('selected', this.selectedIds.has(id));
      }
    },

    // Helper to update select-all checkbox state
    updateSelectAllState(): void {
      const allRows = document.querySelectorAll('[data-guest-id]');
      const allSelected = this.selectedIds.size === allRows.length && allRows.length > 0;
      this.allSelected = allSelected;

      const selectAllWrapper = document.querySelector('thead .gm-checkbox-wrapper');
      if (selectAllWrapper) {
        selectAllWrapper.classList.toggle('all-selected', allSelected);
      }
    },

    // Internal helper to get search query
    _getSearchQuery(): string {
      const searchInput = document.querySelector('.gm-search-input') as HTMLInputElement | null;
      return searchInput?.value || '';
    },
  } as GuestManagerStore & {
    updateRowVisualState(id: string): void;
    updateSelectAllState(): void;
  };
}

// Export the store instance for registration
export const guestManagerStore = createGuestManagerStore();
